<script setup lang="ts">
import { type PropType } from "vue";
import type { CopySize, CopyType, UIColor } from "../../..";

defineProps({
  /*
      Text format
      values:
    */
  type: {
    type: String as PropType<CopyType>,
    required: true,
  },

  size: {
    type: String as PropType<CopySize>,
    required: true,
  },

  /*
      Text color
      values: see src/css/color.css
    */
  color: {
    type: String as PropType<UIColor>,
    required: true,
  },

  /*
      HTML title property
    */
  title: {
    type: String,
    default: "",
  },

  /*
      Toggles white-space: normal;
    */
  wrap: {
    type: Boolean,
    default: false,
  },

  /*
      Toggles white-space: normal;
    */
  preWrap: {
    type: Boolean,
    default: false,
  },

  /*
      text-transform: uppercase
    */
  uppercase: {
    type: Boolean,
    default: false,
  },

  /*
      text-transform: lowercase
    */
  lowercase: {
    type: Boolean,
    default: false,
  },

  /*
      text-transform: capitalize
    */
  capitalize: {
    type: Boolean,
    default: false,
  },

  /*
      width: 100%
    */
  explodeWidth: {
    type: Boolean,
    default: false,
  },

  /*
      text-transform: capitalize
    */
  underline: {
    type: Boolean,
    default: false,
  },
});
</script>

<template>
  <span
    :class="[
      $style.container,
      'componentCopy',
      $style[wrap ? 'wrap' : ''],
      $style[preWrap ? 'prewrap' : ''],
      $style[uppercase ? 'uppercase' : ''],
      $style[lowercase ? 'lowercase' : ''],
      $style[capitalize ? 'capitalize' : ''],
      $style[explodeWidth ? 'explodeWidth' : ''],
      $style[underline ? 'underline' : ''],
    ]"
    :style="{
      color: `var(${color}) !important`,
      'text-decoration-color': `var(${color}) !important`,
    }"
    :title="title"
  >
    <h1
      v-if="type === 'header' && size === 'large'"
      :class="[$style.header, $style.large, 'componentCopyHeader']"
    >
      <slot />
    </h1>

    <h2
      v-if="type === 'header' && size === 'medium'"
      :class="[$style.header, $style.medium, 'componentCopyHeader']"
    >
      <slot />
    </h2>

    <h3
      v-if="type === 'header' && size === 'small'"
      :class="[$style.header, $style.small, 'componentCopyHeader']"
    >
      <slot />
    </h3>

    <p
      v-if="type === 'body'"
      :class="[$style.body, $style[size], 'componentCopyBody']"
    >
      <slot />
    </p>

    <p
      v-if="type === 'bodyStrong'"
      :class="[$style.bodyStrong, $style[size], 'componentCopyBodyStrong']"
    >
      <slot />
    </p>

    <p
      v-if="type === 'bodyInLine'"
      :class="[
        $style.body,
        $style.bodyInLine,
        $style[size],
        'componentCopyBodyInLine',
      ]"
    >
      <slot />
    </p>

    <span
      v-if="type === 'label'"
      :class="[$style.label, $style[size], 'componentCopyLabel']"
    >
      <slot />
    </span>

    <span
      v-if="type === 'number'"
      :class="[$style.number, $style[size], 'componentCopyNumber']"
    >
      <slot />
    </span>
  </span>
</template>

<style module>
.container {
  display: inline-flex;
  background-color: transparent;
  white-space: nowrap !important;
  text-decoration: inherit;
}

.wrap {
  white-space: normal !important;
}

.prewrap {
  white-space: pre-wrap !important;
}

.uppercase {
  text-transform: uppercase !important;
}

.lowercase {
  text-transform: lowercase !important;
}

.capitalize {
  text-transform: capitalize !important;
}

.explodeWidth {
  width: 100%;
}

.underline {
  text-decoration: underline;
}

.header,
.body,
.bodyInLine,
.bodyStrong,
.label,
.number {
  text-decoration: inherit;
  color: inherit;
  text-decoration-color: inherit;
  white-space: inherit;
  font-family: "Albert Sans" !important;
}

.bodyInLine {
  margin-bottom: 0 !important;
}

.header.small {
  font-size: 1rem !important;
  line-height: calc(1rem * 1.48) !important;
  font-weight: 500 !important;
  letter-spacing: calc(-1rem * 0.03) !important;
}

.header.medium {
  font-size: 1.25rem !important;
  line-height: calc(1.25rem * 1.48) !important;
  font-weight: 500 !important;
  letter-spacing: calc(-1.25rem * 0.03) !important;
}

.header.large {
  font-size: 1.75rem !important;
  line-height: calc(1.5rem * 1.48) !important;
  font-weight: 500 !important;
  letter-spacing: calc(-1.75rem * 0.03) !important;
}

.body.small {
  font-size: 0.875rem !important;
  line-height: calc(1rem * 1.48) !important;
  letter-spacing: calc(-0.875rem * 0.03) !important;
}

.body.medium {
  font-size: 1rem !important;
  line-height: calc(1rem * 1.48) !important;
  letter-spacing: calc(-1rem * 0.03) !important;
}

.body.large {
  font-size: 1.25rem !important;
  line-height: calc(1.25rem * 1.48) !important;
  letter-spacing: calc(-1.25rem * 0.03) !important;
}

.bodyStrong.small {
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  line-height: calc(1rem * 1.48) !important;
  letter-spacing: calc(-0.875rem * 0.03) !important;
}

.bodyStrong.medium {
  font-size: 1rem !important;
  font-weight: 500 !important;
  line-height: calc(1rem * 1.48) !important;
  letter-spacing: calc(-1rem * 0.03) !important;
}

.bodyStrong.large {
  font-size: 1.25rem !important;
  font-weight: 500 !important;
  line-height: calc(1.25rem * 1.48) !important;
  letter-spacing: calc(-1.25rem * 0.03) !important;
}

.bodyInLine.small {
  line-height: 0.875rem !important;
}

.bodyInLine.medium {
  line-height: 1rem !important;
}

.bodyInLine.large {
  line-height: 1.25rem !important;
}

.label.small {
  font-size: 0.875rem !important;
  line-height: 0.875rem !important;
  font-weight: 600 !important;
  letter-spacing: calc(-0.875rem * 0.03) !important;
}

.label.medium {
  font-size: 1rem !important;
  line-height: 1rem !important;
  font-weight: 600 !important;
  letter-spacing: calc(-1rem * 0.03) !important;
}

.label.large {
  font-size: 1.125rem !important;
  line-height: 1.125rem !important;
  font-weight: 600 !important;
  letter-spacing: calc(-1.125rem * 0.03) !important;
}

.number.small {
  font-size: 0.875rem !important;
  line-height: calc(0.875rem * 1.48) !important;
  font-weight: 400 !important;
}

.number.medium {
  font-size: 1rem !important;
  line-height: calc(1rem * 1.48) !important;
  font-weight: 400 !important;
}

.number.large {
  font-size: 1.125rem !important;
  line-height: calc(1.125rem * 1.48) !important;
  font-weight: 400 !important;
}

.number.huge {
  font-size: 1.75rem !important;
  line-height: calc(1.75rem * 1.48) !important;
  font-weight: 400 !important;
}
</style>
